import React from "react";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ChevronLeft, Timeline, Menu } from "@mui/icons-material";
import theme from "../utils/theme";
import { userRoles } from "../utils/constant";

const styles = {
  drawer: (open) => ({
    width: open ? 300 : 110,
    height: "100%",
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.grey[200]}`,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
    display: "flex",
    flexDirection: "column",
    transition: "width 0.25s ease-out",
    overflow: "hidden",
    mr: 2,
  }),
  header: (open) => ({
    background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
    p: open ? 2 : 1.5,
    height: 100,
    display: "flex",
    flexDirection: "column",
    alignItems: open ? "stretch" : "center",
    justifyContent: "center",
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background:
        "linear-gradient(135deg, rgba(220, 38, 38, 0.1) 0%, rgba(127, 29, 29, 0.1) 100%)",
      zIndex: 1,
    },
  }),
  headerContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    mb: 1.5,
    position: "relative",
    zIndex: 2,
  },
  logoContainer: {
    display: "flex",
    alignItems: "center",
    gap: 1.5,
  },
  logo: {
    width: 32,
    height: 32,
    borderRadius: 1.5,
    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.paper}E6 100%)`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  },
  collapseButton: {
    color: "white",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.2)",
      transform: "scale(1.05)",
    },
    transition: "all 0.2s ease",
  },
  expandButtonContainer: {
    position: "relative",
    zIndex: 2,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  expandButton: {
    color: "white",
    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.paper}E6 100%)`,
    width: 40,
    height: 40,
    mb: 1,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    "&:hover": {
      background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper}F2 100%)`,
      transform: "scale(1.05)",
    },
    transition: "all 0.2s ease",
  },
  stagesContainer: {
    flex: 1,
    p: 1.5,
    overflow: "auto",
  },
  stageItem: {
    position: "relative",
    mb: 1,
  },
  stageButton: (colors, isInProgress, isCompleted) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    p: 2,
    height: "70px",
    borderRadius: 2,
    background: colors.bg,
    border: `2px solid ${colors.border}`,
    cursor: "pointer",
    position: "relative",
    zIndex: 2,
    boxShadow: colors.shadow || "none",
    outline: colors.glow ? colors.glow : "none",
    transform: colors.transform || "translateX(0) scale(1)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      transform: "translateX(6px) scale(1.03)",
      boxShadow: isInProgress
        ? `0 4px 12px ${theme.palette.info.main}40`
        : isCompleted
        ? `0 4px 12px ${theme.palette.success.main}26`
        : "0 4px 12px rgba(0, 0, 0, 0.1)",
    },
  }),
  stageNumber: (isInProgress, isCompleted, open, isSkipped) => ({
    width: 32,
    height: 32,
    borderRadius: "50%",
    background: isSkipped
      ? theme.palette.grey[700]
      : isInProgress
      ? `linear-gradient(180deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`
      : isCompleted
      ? theme.palette.success.main
      : theme.palette.grey[300],
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    fontWeight: 600,
    fontSize: "0.9rem",
    mr: open ? 2 : 0,
    flexShrink: 0,
    position: "relative",
  }),
  stageDetails: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
  },
  stageName: (colors) => ({
    fontWeight: 500,
    color: colors.text,
    fontSize: "0.9rem",
    lineHeight: 1.3,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
  stageStatus: (colors) => ({
    color: colors.text,
    opacity: 0.7,
    fontSize: "0.7rem",
  }),
};

const RfiNavSidebar = ({
  stageDetails = [],
  onStageSelect,
  open = true,
  onToggleDrawer,
  profile,
}) => {
  const theme = useTheme();

  const getStageColors = (status, isActive, isSkipped) => {
    if (isSkipped)
      return {
        bg: `linear-gradient(135deg, ${theme.palette.grey[300]} 0%, ${theme.palette.grey[200]} 100%)`,
        border: theme.palette.grey[400],
        text: theme.palette.grey[600],
        icon: theme.palette.grey[500],
        shadow: `0 4px 12px ${theme.palette.grey[400]}33`,
        glow: `0 0 0 2px ${theme.palette.grey[400]}1A`,
      };
    if (status === "Completed")
      return {
        bg: `linear-gradient(135deg, ${theme.palette.success.light}40 0%, ${theme.palette.success.light}20 100%)`,
        border: theme.palette.success.main,
        text: theme.palette.success.dark,
        icon: theme.palette.success.main,
      };

    if (isActive)
      return {
        bg: `linear-gradient(135deg, ${theme.palette.info.light} 0%, ${theme.palette.info.light}80 100%)`,
        border: theme.palette.info.main,
        text: theme.palette.info.dark,
        icon: theme.palette.info.main,
        shadow: `0 4px 12px ${theme.palette.info.main}33`,
        glow: `0 0 0 2px ${theme.palette.info.main}1A`,
      };
    return {
      bg: theme.palette.grey[50],
      border: theme.palette.grey[300],
      text: theme.palette.grey[600],
      icon: theme.palette.grey[600],
    };
  };

  return (
    <Box sx={styles.drawer(open)}>
      {/* Header */}
      <Box sx={styles.header(open)}>
        {open ? (
          <>
            <Box sx={styles.headerContent}>
              <Box sx={styles.logoContainer}>
                <Box sx={styles.logo}>
                  <Timeline
                    sx={{ fontSize: 18, color: theme.palette.primary.main }}
                  />
                </Box>
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, fontSize: "1rem", color: "white" }}
                  >
                    RFI Stages
                  </Typography>
                </Box>
              </Box>
              <IconButton
                onClick={onToggleDrawer}
                size="small"
                sx={styles.collapseButton}
              >
                <ChevronLeft sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </>
        ) : (
          <Box sx={styles.expandButtonContainer}>
            <IconButton onClick={onToggleDrawer} sx={styles.expandButton}>
              <Menu sx={{ fontSize: 20, color: theme.palette.primary.main }} />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Stages List */}
      <Box sx={styles.stagesContainer}>
        {stageDetails.map((stage) => {
          const isProductEngineer =
            profile?.role === userRoles.Product_Engineer;
          const isStage_7 = stage.stage_number === "Stage_7";
          const isCompleted = stage.status === "Completed";
          const isActive = stage.is_active || (isStage_7 && isProductEngineer);
          const isSkipped = stage.is_skipped;
          const colors = getStageColors(stage.status, isActive, isSkipped);

          return (
            <Box key={stage.stage_id} sx={styles.stageItem}>
              <Tooltip
                title={
                  open
                    ? ""
                    : `${stage.stage_name} ${
                        isSkipped
                          ? "(Skipped)"
                          : isCompleted
                          ? "(Completed)"
                          : isActive
                          ? "(In Progress)"
                          : "(Pending)"
                      }`
                }
                placement="right"
                arrow
              >
                <Box
                  onClick={() => onStageSelect(stage.stage_id)}
                  sx={styles.stageButton(colors, isActive, isCompleted)}
                >
                  {/* Stage Icon/Number */}
                  <Box
                    sx={styles.stageNumber(
                      isActive,
                      isCompleted,
                      open,
                      isSkipped
                    )}
                  >
                    {stage.stage_number.split("_")[1]}
                  </Box>

                  {/* Stage Details - Only show when expanded */}
                  {open && (
                    <Box sx={styles.stageDetails}>
                      <Typography variant="body2" sx={styles.stageName(colors)}>
                        {stage.stage_name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={styles.stageStatus(colors)}
                      >
                        {isSkipped
                          ? "Skipped"
                          : isCompleted
                          ? "Completed"
                          : isActive
                          ? "In Progress"
                          : "Pending"}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Tooltip>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default RfiNavSidebar;
